import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ChevronDown, ChevronUp, Check } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'sk_jenbach_cookie_consent';

// Check if user has already given consent
export const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading cookie consent:', e);
  }
  return null;
};

// Check if analytics is allowed
export const isAnalyticsAllowed = () => {
  const consent = getStoredConsent();
  return consent?.analytics === true;
};

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always enabled
    analytics: false
  });

  useEffect(() => {
    // Check if consent was already given
    const storedConsent = getStoredConsent();
    if (!storedConsent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consentData) => {
    try {
      const consentRecord = {
        ...consentData,
        timestamp: new Date().toISOString(),
        version: '1.0'
      };
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentRecord));
      
      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
        detail: consentRecord 
      }));
      
      setIsVisible(false);
      
      // Reload to apply analytics changes
      if (consentData.analytics) {
        window.location.reload();
      }
    } catch (e) {
      console.error('Error saving cookie consent:', e);
    }
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true
    });
  };

  const handleSavePreferences = () => {
    saveConsent({
      essential: true,
      analytics: preferences.analytics
    });
  };

  const handleEssentialOnly = () => {
    saveConsent({
      essential: true,
      analytics: false
    });
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm" />
      
      {/* Cookie Consent Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in fade-in slide-in-from-bottom-4 duration-300"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-consent-title"
        >
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#1e3a5f] rounded-full">
                <Cookie className="w-6 h-6 text-white" />
              </div>
              <h2 id="cookie-consent-title" className="text-xl font-bold text-[#1e3a5f]">
                Datenschutzeinstellungen
              </h2>
            </div>
            
            <p className="text-slate-600 text-sm leading-relaxed">
              Auf unserer Webseite werden Cookies verwendet. Einige davon werden zwingend benötigt, 
              während es uns andere ermöglichen, Ihre Nutzererfahrung auf unserer Webseite zu verbessern.
            </p>
          </div>

          {/* Cookie Categories */}
          <div className="px-6 pb-4">
            {/* Essential Cookies - Always enabled */}
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded border-2 border-[#1e3a5f] bg-[#1e3a5f] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="font-medium text-slate-800">Essenziell</span>
              </div>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                Immer aktiv
              </span>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences(prev => ({ 
                    ...prev, 
                    analytics: e.target.checked 
                  }))}
                  className="sr-only peer"
                />
                <div className="w-5 h-5 rounded border-2 border-slate-300 peer-checked:border-[#1e3a5f] peer-checked:bg-[#1e3a5f] flex items-center justify-center transition-colors">
                  {preferences.analytics && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium text-slate-800">Analytics</span>
              </label>
            </div>

            {/* Details Toggle */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:text-[#2d5a8f] mt-4 transition-colors"
            >
              <span>Weitere Informationen anzeigen</span>
              {showDetails ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>

            {/* Details Section */}
            {showDetails && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">Essentielle Cookies</h4>
                  <p>
                    Diese Cookies sind für das Funktionieren der Webseite notwendig und können 
                    nicht deaktiviert werden. Sie werden zum Beispiel für die Benutzerauthentifizierung 
                    und die Verwaltung von Sitzungen verwendet.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">Analytics Cookies</h4>
                  <p>
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Webseite interagieren, 
                    indem sie Informationen anonym sammeln und melden. Wir verwenden Google Analytics, 
                    um die Nutzung unserer Website zu analysieren und zu verbessern.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-6 pt-2 space-y-3">
            <button
              onClick={handleAcceptAll}
              className="w-full py-3 px-4 bg-[#7ba3c9] hover:bg-[#6992b8] text-[#1e3a5f] font-semibold rounded-lg transition-colors"
            >
              Alle akzeptieren
            </button>
            
            <button
              onClick={handleSavePreferences}
              className="w-full py-3 px-4 bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white font-semibold rounded-lg transition-colors"
            >
              Speichern & schließen
            </button>
            
            <button
              onClick={handleEssentialOnly}
              className="w-full py-3 px-4 bg-[#1e3a5f] hover:bg-[#2d5a8f] text-white font-semibold rounded-lg transition-colors"
            >
              Nur essenzielle Cookies akzeptieren
            </button>
          </div>

          {/* Footer Link */}
          <div className="px-6 pb-6 pt-2 border-t border-slate-100">
            <div className="text-center">
              <Link 
                to="/impressum" 
                className="text-sm text-[#1e3a5f] hover:text-[#2d5a8f] hover:underline transition-colors"
              >
                Impressum
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
